import { toast } from "sonner";

import { useAuthStore } from "@/stores/auth.store";
import i18n from "@/lib/i18n";

const isDev = typeof window !== "undefined" && window.location.port === "4200";
const API_BASE_URL = isDev ? "http://localhost:3000/api/v1" : "/api/v1";

function handleUnauthorized(status: number): void {
  if (status === 401 && useAuthStore.getState().isAuthenticated) {
    useAuthStore.getState().logout();
    toast.warning(i18n.t("common.sessionExpired"));
    window.location.href = "/login";
  }
}

export interface ApiError {
  message: string;
  status: number;
  details?: Record<string, string[]>;
}

export class ApiException extends Error {
  status: number;
  details?: Record<string, string[]>;

  constructor(error: ApiError) {
    super(error.message);
    this.name = "ApiException";
    this.status = error.status;
    this.details = error.details;
  }
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
}

export interface DownloadResponse {
  blob: Blob;
  filename: string | null;
}

async function baseRequest<T>(endpoint: string, init: RequestInit): Promise<T> {
  const token = useAuthStore.getState().token;

  const headers = new Headers(init.headers);
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...init,
    headers
  });

  if (!response.ok) {
    handleUnauthorized(response.status);

    let errorData: ApiError;

    try {
      const data = await response.json();
      errorData = {
        message: data.message || "An error occurred",
        status: response.status,
        details: data.details
      };
    } catch {
      errorData = {
        message: response.statusText || "An error occurred",
        status: response.status
      };
    }

    throw new ApiException(errorData);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

async function blobRequest(
  endpoint: string,
  init: RequestInit = {}
): Promise<DownloadResponse> {
  const token = useAuthStore.getState().token;
  const headers = new Headers(init.headers);

  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...init,
    headers
  });

  if (!response.ok) {
    handleUnauthorized(response.status);

    let errorData: ApiError;
    try {
      const data = await response.json();
      errorData = {
        message: data.message || "An error occurred",
        status: response.status,
        details: data.details
      };
    } catch {
      errorData = {
        message: response.statusText || "An error occurred",
        status: response.status
      };
    }

    throw new ApiException(errorData);
  }

  return {
    blob: await response.blob(),
    filename: parseContentDispositionFilename(
      response.headers.get("content-disposition")
    )
  };
}

function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null;

  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);

  const plainMatch = header.match(/filename="?([^";]+)"?/i);
  return plainMatch?.[1] ?? null;
}

async function request<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { body, headers: customHeaders, ...restOptions } = options;

  return baseRequest<T>(endpoint, {
    ...restOptions,
    headers: {
      "Content-Type": "application/json",
      ...customHeaders
    },
    body: body ? JSON.stringify(body) : undefined
  });
}

async function requestFormData<T>(
  endpoint: string,
  formData: FormData
): Promise<T> {
  return baseRequest<T>(endpoint, {
    method: "POST",
    body: formData
  });
}

export const api = {
  get: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: "GET" }),

  post: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: "POST", body }),

  postFormData: <T>(endpoint: string, formData: FormData) =>
    requestFormData<T>(endpoint, formData),

  put: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: "PUT", body }),

  patch: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: "PATCH", body }),

  delete: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: "DELETE" }),

  download: (endpoint: string, options?: RequestInit) =>
    blobRequest(endpoint, { ...options, method: options?.method ?? "GET" })
};
